import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import dayjs from 'dayjs';
import * as actions from 'containers/App/actions';
import {
  updateWeddingChecklistItem,
  updateGeneralChecklistItem,
} from 'api/events';
import {
  updateChecklistItem as updateLeadChecklistItem,
} from 'api/leads';
import { updateTaskStore, updateEventStore } from 'containers/Events/actions';
import { Spinner } from 'components/Loading';
import { useHistory } from 'react-router-dom';
import EmptyTasks from './EmptyTasks';
import './styles.scss';

const TasksWidget = ({ tasksInfo }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [displayTasks, setDisplayTasks] = useState(null);

  const [localNotificationsOpen, setLocalNotificationsOpen] = useState(false);

  const { loginTasks, weddings, leads } = tasksInfo || {};

  // Check loading state
  const isLoading = !loginTasks || !weddings || !leads;

  // Bind action creators
  const {
    getGeneralTasks,
    getProjectTasks,
    getLeadTasks,
    setNotificationsCenterOpen,
    setNotificationsCenterTab,
  } = bindActionCreators(actions, dispatch);

  // Fetch tasks on mount if not already loaded
  useEffect(() => {
    if (!loginTasks) getGeneralTasks();
    if (!weddings) getProjectTasks();
    if (!leads) getLeadTasks();

    if (isLoading) return;

    // Compile all tasks with metadata about their source
    const allTasks = [
      ...(loginTasks.tasks || []).map(task => ({ ...task, source: 'general' })),
      ...(weddings.map(w => w.tasks.map(task => ({
        ...task,
        source: 'project',
        projectName: w.name,
        weddingId: w.id,
      }))).flat()),
      ...(leads.map(l => l.tasks.map(task => ({
        ...task,
        source: 'lead',
        leadName: l.name,
        leadId: l.id,
      }))).flat()),
    ];

    // Filter for urgent/focus tasks only
    const urgentTasks = allTasks.filter(task => {
      // No due date - not urgent
      if (!task.dueDate) return false;
      // Current date is past due date - urgent
      if (dayjs().isAfter(dayjs(task.dueDate))) return true;
      if (dayjs(task.dueDate).isBefore(dayjs().add(7, 'days'))) return true;

      return false;
    });

    // Sort by due date (tasks with due dates first, then by date)
    const sortedTasks = urgentTasks.sort((a, b) => {
      if (!a.dueDate && !b.dueDate) return 0;
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return new Date(a.dueDate) - new Date(b.dueDate);
    });

    setDisplayTasks(sortedTasks);
  }, [loginTasks, weddings, leads, isLoading]);

  useEffect(() => {
    setNotificationsCenterTab('allMyTasks');
    setNotificationsCenterOpen(localNotificationsOpen);
  }, [localNotificationsOpen]);

  function handleGeneralTaskLink() {
    setLocalNotificationsOpen(!localNotificationsOpen);
  }

  // Get action link text based on task type/status
  const getActionLink = (task) => {
    switch(task.source) {
      case 'project':
        return (
          <a href={`/app/project/${task.weddingId}/tools/checklist/all`}>
            {`View ${task.projectName} Checklist`}
          </a>
        );
      case 'lead':
        return (
          <a
            onClick={() =>
              history.push(
                `/app/v2/${task.lead.organizationId}/leads/lead/${task.lead.id}/sales-workflow/${task.id}`,
              )
            }
          >
            {`View ${task.leadName}`}
          </a>
        );
      default:
        return (
          <a onClick={handleGeneralTaskLink}>View Details</a>
        );
    }
  };

  // Handle task completion toggle
  const handleTaskToggle = async (task, e) => {
    e.stopPropagation(); // Prevent triggering handleTaskClick

    const { weddingId, leadId, id: taskId, lead } = task;
    const currentCompleted = task.completed ?? false;

    // Prepare updated data
    const updatedFields = { completed: !currentCompleted };

    // Flip the checkbox right away so it responds to the click, then revert if the save fails
    const setLocalCompleted = completed => setDisplayTasks(prevTasks =>
      prevTasks.map(t => (t.id === taskId ? { ...t, completed } : t)),
    );

    setLocalCompleted(!currentCompleted);

    const itemData = {
      name: task.name,
      details: task.details || '',
      completed: !currentCompleted,
      due_date: task.dueDate,
      timeframe: task.timeframe,
      assignment_note: task.assignmentNote || '',
    };

    // Add wedding-specific fields if applicable
    if (weddingId) {
      itemData.is_private = task.isPrivate ?? false;
      itemData.client_visible = task.clientVisible ?? true;
    }

    let responseUpdate;

    try {
      // Call appropriate API based on task type
      if (weddingId) {
        responseUpdate = await updateWeddingChecklistItem({
          weddingId,
          taskId,
          itemData,
        });
      } else if (leadId) {
        responseUpdate = await updateLeadChecklistItem(
          lead?.organizationId,
          leadId,
          taskId,
          itemData,
        );
      } else {
        responseUpdate = await updateGeneralChecklistItem({
          taskId,
          itemData,
        });
      }

      // Update Redux stores on success
      if (responseUpdate?.ok) {
        const taskType = weddingId ? 'weddings' : leadId ? 'leads' : 'loginTasks';
        dispatch(updateTaskStore({ id: taskId, taskType, data: updatedFields }));
        dispatch(updateEventStore({ id: taskId, data: updatedFields }));
      } else {
        setLocalCompleted(currentCompleted);
      }
    } catch (error) {
      setLocalCompleted(currentCompleted);
      /* eslint-disable no-console */
      console.error('Task completion toggle error:', error);
    }
  };

  function pastDue(dueDate) {
    return dayjs().isAfter(dayjs(dueDate));
  }

  return (
    <div className="dashboard--tasks">
      {/* Spinner is for the first load only — background refetches keep the current list on screen */}
      {!displayTasks ? (
        <Spinner />
      ) : (
        <>
          <div className="tasks-widget-content">
            {displayTasks?.length > 0 ? (
              displayTasks.map(task => (
                <div
                  key={task.id}
                  className={clsx(
                      'task-widget-item',
                      pastDue(task.dueDate) ? 'past-due' : '',
                      task.completed ? 'completed' : '',
                    )}
                >
                  <div
                    className="task-checkbox"
                    onClick={(e) => handleTaskToggle(task, e)}
                  >
                    {task.completed && <FontAwesomeIcon icon={['fa', 'check']} />}
                  </div>
                  <div className="task-content">
                    <div className="task-title-date-wrapper">
                      <div className='task-title'>
                        {task.name}
                      </div>
                      <div className="task-due-date">
                        {dayjs(task.dueDate).format('MMM Do')}
                      </div>
                    </div>
                    <div className="task-action">
                      {getActionLink(task)}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <EmptyTasks />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TasksWidget;

